import { useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { Pill } from '../../layout/Pill.jsx';
import { formatIQD, formatDateTime } from '../../shared/format.js';
import { apiError } from '../../shared/api.js';
import { useAuth } from '../../shared/useAuth.js';
import {
  useCards,
  useBlockCard,
  useReleaseCard,
  useDeleteCard,
  useMarkCardLost
} from './hooks/useCards.js';
import { AddCardModal } from './components/AddCardModal.jsx';
import { TopupModal } from './components/TopupModal.jsx';
import { StatementModal } from './components/StatementModal.jsx';

const STATUS_FILTERS = [
  { key: '', label: 'الكل', tone: 'muted' },
  { key: 'available', label: 'متاحة', tone: 'success' },
  { key: 'active', label: 'مستخدمة', tone: 'orange' },
  { key: 'blocked', label: 'محظورة', tone: 'danger' },
  { key: 'lost', label: 'مفقودة', tone: 'warning' }
];

function StatusPill({ status }) {
  if (status === 'available') return <Pill variant="success">متاحة</Pill>;
  if (status === 'active') return <Pill variant="orange">مستخدمة</Pill>;
  if (status === 'blocked') return <Pill variant="danger">محظورة</Pill>;
  if (status === 'lost') return <Pill variant="warning">مفقودة</Pill>;
  return <Pill variant="muted">{status || '—'}</Pill>;
}

export function CardsScreen() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canManage = hasPermission('reception.issue_card');

  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [topupCard, setTopupCard] = useState(null);
  // كشف حساب البطاقة — للقراءة فقط، فمتاح لكل من يرى هذه الشاشة (الرصيد
  // الحالي معروض في القائمة أصلاً). الخادم يقبل مفاتيح عرض البطاقات ومفاتيح
  // الرؤية المالية القائمة، فلا مفتاح جديد ولا إعادة تسجيل دخول.
  const [statementCard, setStatementCard] = useState(null);
  // نافذة تأكيد داخل التطبيق بدل window.confirm: نافذة المتصفح كانت تُحجب
  // صامتة في الواجهة (مستند مُحاط بـ sandbox/iframe) فلا يُرسل طلب الحذف أبداً.
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  // تسجيل الفقدان خطوة لا رجعة فيها بلا إجراء مضاد — تأكيد قبلها، وسبب الرفض
  // (أطفال داخل القاعة، كارت مسجَّل مسبقاً) يبقى معروضاً داخل النافذة.
  const [confirmLost, setConfirmLost] = useState(null);
  const [lostError, setLostError] = useState('');

  const params = { page, limit: 50 };
  if (status) params.status = status;
  if (q.trim()) params.q = q.trim();

  const cardsQ = useCards(params);
  const cards = cardsQ.data?.items || cardsQ.data || [];
  const total = cardsQ.data?.total ?? cards.length;

  const blockM = useBlockCard();
  const releaseM = useReleaseCard();
  const deleteM = useDeleteCard();
  const lostM = useMarkCardLost();

  // Aggregate counts (from current page only — for a quick visual)
  const counts = cards.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight m-0">البطاقات</h2>
          <div className="text-ink-3 text-xs mt-0.5">
            إدارة بطاقات RFID • {total} بطاقة
          </div>
        </div>
        <div className="flex items-center gap-2">
            {canManage ? (
              <button type="button" className="btn btn-primary" onClick={() => setAddOpen(true)}>
                <Icon name="plus" size={14} /> إضافة بطاقات
              </button>
            ) : null}
          </div>
        </div>

        <Card>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px]">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.key || 'all'}
                  type="button"
                  onClick={() => { setStatus(f.key); setPage(1); }}
                  className={clsx(
                    'px-3 py-1.5 rounded-[8px] text-[12px] font-semibold transition-colors',
                    status === f.key ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-white'
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[200px]">
              <Icon name="search" size={14} className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3 pointer-events-none" />
              <input
                className="input pr-9 text-[12.5px]"
                placeholder="ابحث بـ UID البطاقة..."
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        </Card>

        <div className="card overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-paper-2 text-[11.5px] text-ink-3 uppercase tracking-wider">
              <tr>
                <th className="p-3 text-right">UID</th>
                <th className="p-3 text-right">الحالة</th>
                <th className="p-3 text-right">الرصيد</th>
                <th className="p-3 text-right">الجلسة الحالية</th>
                <th className="p-3 text-right">آخر إصدار</th>
                <th className="p-3 text-right">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {cardsQ.isLoading ? (
                <tr><td colSpan={6} className="p-6 text-center text-ink-3">جاري التحميل...</td></tr>
              ) : cards.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-ink-3">
                  لا توجد بطاقات{status ? ` بحالة "${STATUS_FILTERS.find((f) => f.key === status)?.label}"` : ''}
                  {canManage ? <div className="mt-2"><button className="btn btn-sm btn-primary" onClick={() => setAddOpen(true)}>إضافة بطاقات الآن</button></div> : null}
                </td></tr>
              ) : (
                cards.map((c) => {
                  const id = c._id || c.id;
                  const inSession = !!c.currentSessionId;
                  return (
                    <tr key={id} className="border-t border-line hover:bg-paper-2">
                      <td className="p-3 font-mono font-bold">{c.uid}</td>
                      <td className="p-3"><StatusPill status={c.status} /></td>
                      <td className="p-3 font-bold">{formatIQD(c.balance || 0)}</td>
                      <td className="p-3 text-ink-2 text-[12px]">
                        {inSession ? <span className="text-orange-deep">جلسة #{String(c.currentSessionId._id || c.currentSessionId).slice(-6)}</span> : '—'}
                      </td>
                      <td className="p-3 text-ink-3 text-[11.5px]">{c.issuedAt ? formatDateTime(c.issuedAt) : '—'}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {/* كشف الحساب متاح دائماً — حتى للكروت المفقودة
                              والمحظورة: سجلّها المالي هو أول ما يُسأل عنه. */}
                          <button
                            type="button"
                            className="btn btn-sm"
                            data-testid={`statement-${c.uid}`}
                            onClick={() => setStatementCard(c)}
                            title="كشف حساب البطاقة — كل حركة غيّرت الرصيد"
                          >
                            <Icon name="reports" size={12} /> كشف حساب
                          </button>
                        {canManage ? (
                          <>
                            {c.status === 'lost' ? null : (
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => setTopupCard(c)}
                                title="شحن"
                              >
                                +
                              </button>
                            )}
                            {c.status === 'active' ? (
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => releaseM.mutate(id)}
                                disabled={releaseM.isPending}
                                title="تحرير"
                              >
                                تحرير
                              </button>
                            ) : c.status === 'blocked' ? (
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => releaseM.mutate(id)}
                                disabled={releaseM.isPending}
                                title="إعادة تفعيل"
                              >
                                تفعيل
                              </button>
                            ) : c.status === 'lost' ? (
                              // الكارت المفقود عاد: يرجع «متاحة» بنفس مسار الاسترجاع،
                              // فيحتفظ برصيده وسجلّ جلساته وفواتيره كما هو.
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => releaseM.mutate(id)}
                                disabled={releaseM.isPending}
                                title="تم العثور على الكارت — إرجاعه للمتاحة"
                              >
                                عُثر عليها
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm text-red"
                                onClick={() => blockM.mutate(id)}
                                disabled={blockM.isPending || inSession}
                                title="حظر"
                              >
                                حظر
                              </button>
                            )}
                            {c.status === 'lost' ? null : (
                              <button
                                type="button"
                                className="btn btn-sm"
                                style={{ color: '#92400E' }}
                                onClick={() => { setLostError(''); setConfirmLost(c); }}
                                disabled={lostM.isPending}
                                title="تسجيل الكارت كمفقود"
                              >
                                مفقودة
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm text-red hover:bg-red/5"
                              onClick={() => { setDeleteError(''); setConfirmDelete(c); }}
                              disabled={deleteM.isPending || inSession}
                              title="حذف"
                            >
                              <Icon name="trash" size={12} />
                            </button>
                          </>
                        ) : (
                          <span className="text-ink-3 text-[11.5px]">للقراءة فقط</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Counts strip */}
      {cards.length > 0 ? (
        <div className="flex items-center gap-2 flex-wrap text-[11.5px] text-ink-3">
          <span>إجمالي معروض: <b className="text-ink">{cards.length}</b></span>
          <span>•</span>
          <span>متاحة: <b className="text-green-700">{counts.available || 0}</b></span>
          <span>•</span>
          <span>مستخدمة: <b className="text-orange-deep">{counts.active || 0}</b></span>
          <span>•</span>
          <span>محظورة: <b className="text-red">{counts.blocked || 0}</b></span>
          <span>•</span>
          <span>مفقودة: <b style={{ color: '#92400E' }}>{counts.lost || 0}</b></span>
        </div>
      ) : null}

      <AddCardModal open={addOpen} onClose={() => setAddOpen(false)} />
      <TopupModal open={!!topupCard} card={topupCard} onClose={() => setTopupCard(null)} />
      {statementCard ? (
        <StatementModal card={statementCard} onClose={() => setStatementCard(null)} />
      ) : null}

      {confirmLost ? (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgba(0,0,0,.45)' }}>
          <div className="card p-5 w-[460px] max-w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[15px] font-bold m-0">تسجيل البطاقة كمفقودة</h3>
            <div className="text-[13px] text-ink-2">
              هل تريد تسجيل البطاقة <b className="font-mono">{confirmLost.uid}</b> كمفقودة؟
            </div>
            <div className="text-[11.5px] text-ink-3">
              تنتقل البطاقة إلى تبويب «المفقودة» فوراً وتبقى فيه بعد التحديث أو إعادة التشغيل،
              ويرفض النظام أي دخول أو بيع أو تعبئة عليها. لا يُحذف شيء: الرصيد والجلسات
              والفواتير القديمة تبقى مرتبطة بها، وإن عُثر عليها لاحقاً يرجعها زر «عُثر عليها».
            </div>
            {Number(confirmLost.balance || 0) > 0 ? (
              <div className="text-[12.5px] font-semibold border border-line rounded-[8px] p-2.5" style={{ background: '#FFF7ED', color: '#92400E' }}>
                عليها رصيد {formatIQD(confirmLost.balance)} — يبقى محفوظاً على البطاقة ولا يُصرف.
              </div>
            ) : null}
            {lostError ? (
              <div className="text-[12.5px] text-red font-semibold border border-red/30 bg-red/5 rounded-[8px] p-2.5">
                {lostError}
              </div>
            ) : null}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className="btn"
                onClick={() => { setConfirmLost(null); setLostError(''); }}
                disabled={lostM.isPending}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={lostM.isPending}
                onClick={async () => {
                  setLostError('');
                  try {
                    await lostM.mutateAsync({ id: confirmLost._id || confirmLost.id });
                    // الانتقال للتبويب يجعل النتيجة مرئية فوراً بدل أن يبحث
                    // المستخدم عن البطاقة بنفسه ويظن أن العملية لم تنجح.
                    setStatus('lost');
                    setPage(1);
                    setConfirmLost(null);
                  } catch (err) {
                    setLostError(apiError(err));
                  }
                }}
              >
                {lostM.isPending ? 'جاري...' : 'تسجيل كمفقودة'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDelete ? (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgba(0,0,0,.45)' }}>
          <div className="card p-5 w-[460px] max-w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[15px] font-bold m-0">حذف البطاقة</h3>
            <div className="text-[13px] text-ink-2">
              هل تريد حذف البطاقة <b className="font-mono">{confirmDelete.uid}</b>؟
            </div>
            <div className="text-[11.5px] text-ink-3">
              لا يُحذف كارت عليه رصيد أو أطفال داخل القاعة أو فواتير غير مسددة. والكارت الذي له
              سجل سابق (جلسات أو فواتير) يُلغى ويُخفى من القائمة بدلاً من حذفه، كي لا يفقد سجله المالي مرجعه.
            </div>
            {deleteError ? (
              <div className="text-[12.5px] text-red font-semibold border border-red/30 bg-red/5 rounded-[8px] p-2.5">
                {deleteError}
              </div>
            ) : null}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className="btn"
                onClick={() => { setConfirmDelete(null); setDeleteError(''); }}
                disabled={deleteM.isPending}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={deleteM.isPending}
                onClick={async () => {
                  setDeleteError('');
                  try {
                    await deleteM.mutateAsync(confirmDelete._id || confirmDelete.id);
                    setConfirmDelete(null);
                  } catch (err) {
                    // سبب الرفض يبقى معروضاً في النافذة، لا يختفي مع التوست.
                    setDeleteError(apiError(err));
                  }
                }}
              >
                {deleteM.isPending ? 'جاري...' : 'حذف'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
